"use client";
import React, { FC, useState } from "react";

import { PortableTextProps } from "@portabletext/react";
import { track } from "@vercel/analytics";

import { Button } from "components/button";
import { TextBox } from "components/text-box";

import { LineBar } from "./line-bar";
import { StackedLineBar } from "./stacked-line-bar";

interface ExpandableCalloutProps {
  caption?: PortableTextProps["value"];
  title?: string;
  translations: {
    allTestedWebsites: string;
    expand: string;
    failedTests: string;
    hide: string;
    okTests: string;
    website: string;
  };
  value: number;
  values?: Array<number>;
}

const ExpandableCallout: FC<ExpandableCalloutProps> = ({
  caption,
  title,
  translations,
  value,
  values,
}) => {
  const t = translations;
  const [lineBarExpanded, setLineBarExpanded] = useState(false);
  const hiddenHeadingRef = React.useRef<HTMLHeadingElement>(null);

  const handleMobileExpandClick = () => {
    // Focus on the first item in the list for screen readers
    if (!lineBarExpanded) {
      setTimeout(() => {
        if (hiddenHeadingRef.current)
          hiddenHeadingRef.current.focus({ preventScroll: true });
      }, 200);
    }
    setLineBarExpanded(!lineBarExpanded);
  };

  return (
    <TextBox className="my-8" size="full" useProse>
      <div className="mb-6 flex flex-row flex-wrap items-center justify-between gap-4">
        {title && (
          <span className="wrap-text text-2xl font-semibold">{title}</span>
        )}
        {values && (
          <Button
            aria-expanded={lineBarExpanded}
            className="hidden h-fit min-w-[150px] shrink-0 sm:shrink-[initial] md:flex"
            colorVariant="secondary"
            onClick={() => {
              track("Ecommerce expand callout", { value: !lineBarExpanded });
              setLineBarExpanded(!lineBarExpanded);
            }}
            type="button"
            variant="small"
          >
            {lineBarExpanded ? t.hide : t.expand}
          </Button>
        )}
      </div>

      {!lineBarExpanded && <LineBar value={value} />}
      {lineBarExpanded && values && (
        <>
          <h3
            className="sr-only text-lg font-normal"
            ref={hiddenHeadingRef}
            tabIndex={-1}
          >
            {t.allTestedWebsites}
          </h3>
          <StackedLineBar
            caption={caption}
            className="mt-8"
            component="div"
            translations={translations}
            values={values}
          />
        </>
      )}
      {values && (
        <Button
          aria-expanded={lineBarExpanded}
          className="mt-8 flex h-fit min-w-[150px] shrink-0 sm:shrink-[initial] md:hidden"
          colorVariant="secondary"
          onClick={handleMobileExpandClick}
          type="button"
          variant="small"
        >
          {lineBarExpanded ? t.hide : t.expand}
        </Button>
      )}
    </TextBox>
  );
};

export default ExpandableCallout;
